import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API, { getMediaURL } from '../../../context/API';

// Types
export interface CustomizationOption {
  id: number | string;
  option: string;
  description?: string;
  price?: number;
  sequence_number?: number;
  sold_out?: boolean;
  customization?: number | string;
  item?: any;
}

export interface RelatedItem {
  id?: number | string;
  item?: {
    id?: number | string;
    item?: string;
  } | string;
  picture?: string;
  name?: string;
  sequence_number?: number;
}

export interface Customization {
  id: number;
  customization: string;
  note?: string;
  sequence_number?: number;
  max_quantity?: number;
  min_quantity?: number;
  multiple_selections?: boolean;
  sold_out?: boolean;
  multiple_selections_quantity?: number;
  option?: CustomizationOption[];
  options?: CustomizationOption[];
  related_items?: RelatedItem[];
  merchant?: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomizationData {
  customization: string;
  options?: CustomizationOption[];
  option?: CustomizationOption[];
  note?: string;
  sequence_number?: number;
  max_quantity?: number;
  min_quantity?: number;
  multiple_selections?: boolean;
  sold_out?: boolean;
  multiple_selections_quantity?: number;
  related_items?: RelatedItem[];
}

export interface CustomizationsState {
  customizations: Customization[];
  filteredCustomizations: Customization[];
  selectedItem: number | null;
  loading: boolean;
  error: string | null;
  refreshing: boolean;
  lastUpdated: string | null;
  expandedIndex: number | null;
  page: number;
  hasMore: boolean;
  totalPages: number;
  total: number;
  dataFetched: boolean;
  showCreateForm: boolean;
  submitting: boolean;
  customizationData: CustomizationData | null;
  showDeleteDialog: boolean;
  customizationToDelete: Customization | null;
  searchQuery: string;
}

const initialState: CustomizationsState = {
  customizations: [],
  filteredCustomizations: [],
  selectedItem: null,
  loading: true,
  error: null,
  refreshing: false,
  lastUpdated: null,
  expandedIndex: null,
  page: 1,
  hasMore: true,
  totalPages: 1,
  total: 0,
  dataFetched: false,
  showCreateForm: false,
  submitting: false,
  customizationData: null,
  showDeleteDialog: false,
  customizationToDelete: null,
  searchQuery: '',
};

// Async Thunks
export const fetchCustomizations = createAsyncThunk(
  'customizations/fetchCustomizations',
  async ({ pageNum, search }: { pageNum: number; search?: string }, { rejectWithValue }) => {
    try {
      let url = `/customizations?page=${pageNum}&limit=20`;
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }
      
      const response = await API.get(url);
      
      if (response.data.success) {
        const customizationsData = response.data.data.map((customization: any) => {
          const processedCustomization: Customization = {
            ...customization,
            sold_out: Boolean(customization.sold_out),
            options: (customization.options || customization.option || [])
              .map((option: any) => ({
                ...option,
                sold_out: Boolean(option.sold_out),
                item: { ...option.item },
              }))
              .sort((a: CustomizationOption, b: CustomizationOption) => 
                (a.sequence_number || 0) - (b.sequence_number || 0)
              ),
            option: (customization.option || customization.options || [])
              .map((option: any) => ({
                ...option,
                sold_out: Boolean(option.sold_out),
                item: { ...option.item },
              }))
              .sort((a: CustomizationOption, b: CustomizationOption) => 
                (a.sequence_number || 0) - (b.sequence_number || 0)
              ),
            related_items: (customization.related_items || []).map((item: any) => ({
              ...item,
              picture: getMediaURL(item.picture),
            })),
          };
          return processedCustomization;
        });

        // Sort by ID in descending order (newest first)
        const sortedCustomizationsData = [...customizationsData].sort((a: Customization, b: Customization) => b.id - a.id);

        return {
          data: sortedCustomizationsData,
          pagination: response.data.pagination,
          page: pageNum,
          search: search || '',
        };
      } else {
        return rejectWithValue('Failed to fetch customizations.');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'An error occurred while fetching customizations.');
    }
  }
);

export const createCustomization = createAsyncThunk(
  'customizations/createCustomization',
  async (data: CustomizationData, { rejectWithValue }) => {
    try {
      const formattedData = {
        customization: data.customization?.trim() || '',
        options: [],
        note: data.note?.trim() || '',
        sequence_number: parseInt(String(data.sequence_number)) || 1,
        max_quantity: parseInt(String(data.max_quantity)) || 0,
        min_quantity: parseInt(String(data.min_quantity)) || 0,
        option_limit: 0,
        multiple_selections: Boolean(data.multiple_selections),
        sold_out: false,
        multiple_selections_quantity: parseInt(String(data.multiple_selections_quantity)) || 0,
        related_items: [],
        merchant: {},
      };

      const response = await API.post('/customizations', formattedData);

      if (response.status === 201 || response.data.success) {
        const responseData = response.data.data || response.data;
        return {
          id: responseData.id,
          customization: responseData.customization,
          options: responseData.options || responseData.option || [],
          option: responseData.option || responseData.options || [],
          note: responseData.note || '',
          sequence_number: responseData.sequence_number || 1,
          max_quantity: responseData.max_quantity || 0,
          min_quantity: responseData.min_quantity || 0,
          multiple_selections: responseData.multiple_selections || false,
          sold_out: Boolean(responseData.sold_out),
          multiple_selections_quantity: responseData.multiple_selections_quantity || 0,
          related_items: responseData.related_items || [],
        };
      } else {
        return rejectWithValue('Failed to create customization.');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create customization.');
    }
  }
);

export const updateCustomization = createAsyncThunk(
  'customizations/updateCustomization',
  async ({ id, data, type }: { id: number; data: any; type: string }, { rejectWithValue }) => {
    try {
      let response;
      let endpoint = `/customizations/${id}`;
      let payload = data;

      if (type === 'name') {
        try {
          response = await API.put(`/customizations/${id}/name`, {
            customization: data.customization.trim(),
          });
        } catch (nameError) {
          response = await API.put(`/customizations/${id}`, {
            customization: data.customization.trim(),
          });
        }
      } else if (type === 'options') {
        endpoint = `/customizations/${id}/options`;
        payload = {
          options: data.map((option: any) => {
            if (typeof option === 'object' && option !== null && 'id' in option) {
              return {
                id: option.id.toString(),
                sequence_number: option.sequence_number,
              };
            }
            return option.toString();
          }),
        };
        response = await API.put(endpoint, payload);
      } else if (type === 'addtoitems') {
        payload = {
          customization: data.customization,
          options: Array.isArray(data.options)
            ? data.options.map((option: any) =>
                typeof option === 'object' && option !== null
                  ? option.id ? option.id.toString() : option.toString()
                  : option.toString()
              )
            : [],
          note: data.note || '',
          sequence_number: data.sequence_number || 0,
          max_quantity: data.max_quantity || 0,
          min_quantity: data.min_quantity || 0,
          option_limit: 0,
          multiple_selections: data.multiple_selections || false,
          sold_out: data.sold_out !== undefined ? Boolean(data.sold_out) : false,
          multiple_selections_quantity: data.multiple_selections_quantity || 0,
          related_items: (data.related_items || []).length > 0
            ? (data.related_items || [])
                .map((relatedItem: any, index: number) => ({
                  id: relatedItem.item && relatedItem.item.id
                    ? relatedItem.item.id.toString()
                    : relatedItem.id ? relatedItem.id.toString() : '',
                  sequence_number: relatedItem.sequence_number || index.toString(),
                }))
                .filter((item: any) => item.id !== '')
            : [],
          merchant: {},
        };
        response = await API.put(endpoint, payload);
      } else if (type === 'customizationrules') {
        endpoint = `/customizations/${id}/rules`;
        payload = {
          note: data.note || '',
          sequence_number: data.sequence_number || 0,
          max_quantity: data.max_quantity || 0,
          min_quantity: data.min_quantity || 0,
          option_limit: data.option_limit || 0,
          multiple_selections: data.multiple_selections || true,
          sold_out: data.sold_out || false,
          multiple_selections_quantity: data.multiple_selections_quantity || 0,
          merchant: data.merchant || {},
        };
        response = await API.put(endpoint, payload);
      } else {
        response = await API.put(endpoint, payload);
      }

      if (response.data.success) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to update customization.');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update customization.');
    }
  }
);

export const deleteCustomization = createAsyncThunk(
  'customizations/deleteCustomization',
  async (id: number, { rejectWithValue }) => {
    try {
      await API.delete(`/customizations/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete customization.');
    }
  }
);

export const cloneCustomization = createAsyncThunk(
  'customizations/cloneCustomization',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await API.get(`/customizations/clone/${id}`);
      if (response.data.success) {
        return response.data.data;
      } else {
        return rejectWithValue('Failed to clone customization.');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to clone customization.');
    }
  }
);

export const bulkUpdateCustomizationOrder = createAsyncThunk(
  'customizations/bulkUpdateCustomizationOrder',
  async (updates: { id: number; sequence_number: number }[], { rejectWithValue }) => {
    try {
      const response = await API.put('/customizations/bulk-update', { updates });
      if (response.data.success) {
        return updates;
      }
      return rejectWithValue('Failed to reorder customizations.');
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reorder customizations.');
    }
  }
);

export const updateOptionSoldOut = createAsyncThunk(
  'customizations/updateOptionSoldOut',
  async ({ option, updatedOption }: { option: CustomizationOption; updatedOption: CustomizationOption }, { rejectWithValue }) => {
    try {
      const cleanPayload = {
        option: updatedOption.option,
        description: updatedOption.description,
        price: updatedOption.price,
        sequence_number: updatedOption.sequence_number,
        sold_out: updatedOption.sold_out,
        customization: updatedOption.customization,
      };

      await API.put(`/options/${option.id}`, cleanPayload);
      return { optionId: option.id, sold_out: updatedOption.sold_out };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update option.');
    }
  }
);

// Slice
const customizationsSlice = createSlice({
  name: 'customizations',
  initialState,
  reducers: {
    setFilteredCustomizations: (state, action: PayloadAction<Customization[]>) => {
      state.filteredCustomizations = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<number | null>) => {
      state.selectedItem = action.payload;
    },
    setExpandedIndex: (state, action: PayloadAction<number | null>) => {
      state.expandedIndex = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setShowCreateForm: (state, action: PayloadAction<boolean>) => {
      state.showCreateForm = action.payload;
    },
    setCustomizationData: (state, action: PayloadAction<CustomizationData | null>) => {
      state.customizationData = action.payload;
    },
    setShowDeleteDialog: (state, action: PayloadAction<boolean>) => {
      state.showDeleteDialog = action.payload;
    },
    setCustomizationToDelete: (state, action: PayloadAction<Customization | null>) => {
      state.customizationToDelete = action.payload;
    },
    setRefreshing: (state, action: PayloadAction<boolean>) => {
      state.refreshing = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    updateCustomizationInList: (state, action: PayloadAction<Customization>) => {
      const index = state.customizations.findIndex(c => c.id === action.payload.id);
      if (index !== -1) {
        state.customizations[index] = action.payload;
      }
      const filteredIndex = state.filteredCustomizations.findIndex(c => c.id === action.payload.id);
      if (filteredIndex !== -1) {
        state.filteredCustomizations[filteredIndex] = action.payload;
      }
    },
    updateOptionInCustomization: (state, action: PayloadAction<{ customizationId: number; optionId: number | string; sold_out: boolean }>) => {
      const { customizationId, optionId, sold_out } = action.payload;
      const updateOption = (customization: Customization) => {
        if (customization.option) {
          customization.option = customization.option.map(opt =>
            opt.id === optionId ? { ...opt, sold_out } : opt
          );
        }
        if (customization.options) {
          customization.options = customization.options.map(opt =>
            opt.id === optionId ? { ...opt, sold_out } : opt
          );
        }
      };

      const customization = state.customizations.find(c => c.id === customizationId);
      if (customization) {
        updateOption(customization);
      }

      const filteredCustomization = state.filteredCustomizations.find(c => c.id === customizationId);
      if (filteredCustomization) {
        updateOption(filteredCustomization);
      }
    },
    reorderOptions: (state, action: PayloadAction<{ customizationId: number; reorderedOptions: CustomizationOption[] }>) => {
      const { customizationId, reorderedOptions } = action.payload;
      const updateOptions = (customization: Customization) => {
        customization.option = reorderedOptions;
        customization.options = reorderedOptions;
      };

      const customization = state.customizations.find(c => c.id === customizationId);
      if (customization) {
        updateOptions(customization);
      }

      const filteredCustomization = state.filteredCustomizations.find(c => c.id === customizationId);
      if (filteredCustomization) {
        updateOptions(filteredCustomization);
      }
    },
    resetCustomizations: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customizations
      .addCase(fetchCustomizations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomizations.fulfilled, (state, action) => {
        state.loading = false;
        state.refreshing = false;
        const { data, pagination, page, search } = action.payload;
        
        if (page === 1 || state.searchQuery !== search) {
          state.customizations = data;
          state.filteredCustomizations = data;
          state.searchQuery = search || '';
        } else {
          state.customizations = [...state.customizations, ...data];
          state.filteredCustomizations = [...state.filteredCustomizations, ...data];
        }
        
        if (state.selectedItem) {
          const updatedCustomization = state.customizations.find(
            (c) => c.id === state.selectedItem
          );
          if (updatedCustomization) {
            state.customizationData = {
              customization: updatedCustomization.customization,
              options: updatedCustomization.options || updatedCustomization.option,
              note: updatedCustomization.note,
              max_quantity: updatedCustomization.max_quantity,
              min_quantity: updatedCustomization.min_quantity,
              multiple_selections: updatedCustomization.multiple_selections,
              sold_out: updatedCustomization.sold_out,
              multiple_selections_quantity: updatedCustomization.multiple_selections_quantity,
              related_items: updatedCustomization.related_items || [],
            };
          }
        }
        state.totalPages = pagination.totalPages;
        state.total = pagination.total || 0;
        state.page = page;
        state.hasMore = page < pagination.totalPages;
        state.lastUpdated = new Date().toLocaleString();
        state.dataFetched = true;
      })
      .addCase(fetchCustomizations.rejected, (state, action) => {
        state.loading = false;
        state.refreshing = false;
        state.error = action.payload as string;
      })
      // Create customization
      .addCase(createCustomization.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createCustomization.fulfilled, (state, action) => {
        state.submitting = false;
        state.customizations = [action.payload, ...state.customizations];
        state.filteredCustomizations = [action.payload, ...state.filteredCustomizations];
        state.showCreateForm = false;
        state.customizationData = null;
        state.selectedItem = null;
      })
      .addCase(createCustomization.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })
      // Update customization
      .addCase(updateCustomization.pending, (state) => {
        // Optional: Set loading state if needed
      })
      .addCase(updateCustomization.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.customizations.findIndex(c => c.id === updated.id);
        if (index !== -1) {
          state.customizations[index] = { ...state.customizations[index], ...updated };
        }
        const filteredIndex = state.filteredCustomizations.findIndex(c => c.id === updated.id);
        if (filteredIndex !== -1) {
          state.filteredCustomizations[filteredIndex] = { ...state.filteredCustomizations[filteredIndex], ...updated };
        }
        if (state.customizationData && state.selectedItem === updated.id) {
          state.customizationData = { ...state.customizationData, ...updated };
        }
      })
      .addCase(updateCustomization.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Delete customization
      .addCase(deleteCustomization.pending, (state) => {
        // Optional: Set loading state if needed
      })
      .addCase(deleteCustomization.fulfilled, (state, action) => {
        const id = action.payload;
        state.customizations = state.customizations.filter(c => c.id !== id);
        state.filteredCustomizations = state.filteredCustomizations.filter(c => c.id !== id);
        if (state.selectedItem === id) {
          state.selectedItem = null;
          state.customizationData = null;
        }
        state.showDeleteDialog = false;
        state.customizationToDelete = null;
      })
      .addCase(deleteCustomization.rejected, (state, action) => {
        state.error = action.payload as string;
        state.showDeleteDialog = false;
      })
      // Clone customization
      .addCase(cloneCustomization.pending, (state) => {
        // Optional: Set loading state if needed
      })
      .addCase(cloneCustomization.fulfilled, (state, action) => {
        state.customizations = [action.payload, ...state.customizations];
        state.filteredCustomizations = [action.payload, ...state.filteredCustomizations];
      })
      .addCase(cloneCustomization.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Update option sold out
      .addCase(updateOptionSoldOut.pending, (state) => {
        // Optional: Set loading state if needed
      })
      .addCase(updateOptionSoldOut.fulfilled, (state, action) => {
        const { optionId, sold_out } = action.payload;
        // Update in all customizations
        state.customizations.forEach(customization => {
          if (customization.option) {
            const optionIndex = customization.option.findIndex(opt => opt.id === optionId);
            if (optionIndex !== -1) {
              customization.option[optionIndex].sold_out = sold_out;
            }
          }
          if (customization.options) {
            const optionIndex = customization.options.findIndex(opt => opt.id === optionId);
            if (optionIndex !== -1) {
              customization.options[optionIndex].sold_out = sold_out;
            }
          }
        });
        // Update in filtered customizations
        state.filteredCustomizations.forEach(customization => {
          if (customization.option) {
            const optionIndex = customization.option.findIndex(opt => opt.id === optionId);
            if (optionIndex !== -1) {
              customization.option[optionIndex].sold_out = sold_out;
            }
          }
          if (customization.options) {
            const optionIndex = customization.options.findIndex(opt => opt.id === optionId);
            if (optionIndex !== -1) {
              customization.options[optionIndex].sold_out = sold_out;
            }
          }
        });
      })
      .addCase(updateOptionSoldOut.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilteredCustomizations,
  setSelectedItem,
  setExpandedIndex,
  setPage,
  setShowCreateForm,
  setCustomizationData,
  setShowDeleteDialog,
  setCustomizationToDelete,
  setRefreshing,
  setSearchQuery,
  updateCustomizationInList,
  updateOptionInCustomization,
  reorderOptions,
  resetCustomizations,
} = customizationsSlice.actions;

export default customizationsSlice.reducer;

